/**
 * 05 · P&L navegable: la tabla, sin gráfico a propósito. Números
 * monoespaciados a la derecha, totales sobre fondo, color solo en las columnas
 * de variación. Below EBITDA, Media Investment e intereses no tienen plan (el
 * presupuesto no los abre): salen "sin plan".
 */

import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { computeTable, isFavorable, type PnlTableRow } from '../../../../../shared/finance-pnl';
import type { PnlDashboardData } from '../../../../../shared/types/finance-pnl';
import { C, type PnlFmt } from './pnl-format';
import { PnlNote, SectionHead } from './pnl-ui';

const ROW_KEY: Record<PnlTableRow['key'], string> = {
  grossRevenue: 'budget_control.lines.grossRevenue',
  mediaInvestment: 'budget_control.lines.mediaInvestment',
  revenue: 'budget_control.lines.revenue',
  cogs: 'budget_control.lines.cogs',
  grossProfit: 'budget_control.lines.grossProfit',
  gya: 'budget_control.lines.gya',
  investment: 'budget_control.lines.investment',
  bonos: 'budget_control.lines.bonos',
  taxes: 'budget_control.lines.taxes',
  ebitda: 'budget_control.lines.ebitda',
  otherIncomes: 'budget_control.lines.otherIncomes',
  existencial: 'budget_control.lines.existencial',
  otherExpenses: 'budget_control.lines.otherExpenses',
  ebitdaFinal: 'budget_control.lines.ebitdaFinal',
  interestExpense: 'budget_control.lines.interestExpense',
  resultAfterInterest: 'budget_control.lines.resultAfterInterest',
};

export function PnlTable({ data, month, fmt, notes }: { data: PnlDashboardData; month: number; fmt: PnlFmt; notes: boolean }) {
  const { t } = useTranslation();
  const rows = computeTable(data, month);
  const num = (v: number | null) => (v === null ? t('budget_control.table.no_plan') : fmt.num(v / 1000));
  const favStyle = (fav: boolean | null) => (fav === null ? undefined : { color: fav ? C.fav : C.unfav });
  const td = 'whitespace-nowrap border-b border-border px-2.5 py-1.5 text-right font-mono text-[13px] tabular-nums';
  const th = 'whitespace-nowrap border-b border-border px-2.5 py-1.5 text-right text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground';

  return (
    <section>
      <SectionHead num="06" title={t('budget_control.table.title')} sub={t('budget_control.table.subtitle')} />
      <Card className="p-4 shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] border-collapse text-[13px]">
            <thead>
              <tr>
                <th className={cn(th, 'text-left')}>{t('budget_control.table.line')}</th>
                <th className={th}>{t('budget_control.table.real', { month: fmt.monthShort(month) })}</th>
                <th className={th}>{t('budget_control.table.budget', { month: fmt.monthShort(month) })}</th>
                <th className={th}>{t('budget_control.table.var')}</th>
                <th className={th}>{t('budget_control.table.var_pct')}</th>
                <th className={th}>{month > 1 ? fmt.monthShort(month - 1) : '–'}</th>
                <th className={th}>{t('budget_control.table.var_pct')}</th>
                <th className={th}>{t('budget_control.table.ytd_real')}</th>
                <th className={th}>{t('budget_control.table.ytd_budget')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const d = r.budget === null ? null : r.actual - r.budget;
                const fav = d === null ? null : isFavorable(r.up, d);
                const dp = d === null || !r.budget ? null : (d / Math.abs(r.budget)) * 100;
                const dl = r.prev === null || !r.prev ? null : ((r.actual - r.prev) / Math.abs(r.prev)) * 100;
                const favl = dl === null ? null : isFavorable(r.up, dl);
                const dlTxt = dl === null ? '–' : Math.abs(dl) > 300 ? fmt.sgnK(r.actual - (r.prev ?? 0)) : fmt.sgnPct(dl);
                return (
                  <tr key={r.key} className={cn(r.total && 'bg-muted font-semibold', r.below && 'bg-muted/40', r.sep && 'border-t-2 border-t-border')}>
                    <td className={cn(td, 'text-left font-sans')}>{t(ROW_KEY[r.key])}</td>
                    <td className={td}>{fmt.num(r.actual / 1000)}</td>
                    <td className={cn(td, r.budget === null && 'italic')} style={r.budget === null ? { color: C.warn } : undefined}>{num(r.budget)}</td>
                    <td className={td} style={favStyle(fav)}>{d === null ? '–' : fmt.sgn(d / 1000)}</td>
                    <td className={td} style={favStyle(fav)}>{dp === null ? '–' : fmt.sgnPct(dp)}</td>
                    <td className={td}>{r.prev === null ? '–' : fmt.num(r.prev / 1000)}</td>
                    <td className={td} style={favStyle(favl)}>{dlTxt}</td>
                    <td className={td}>{fmt.num(r.ytdActual / 1000)}</td>
                    <td className={cn(td, r.ytdBudget === null && 'italic')} style={r.ytdBudget === null ? { color: C.warn } : undefined}>{num(r.ytdBudget)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      <PnlNote
        show={notes}
        items={[
          { tag: 'form', title: t('budget_control.notes.table.form_title'), text: t('budget_control.notes.table.form_text') },
          { tag: 'warn', title: t('budget_control.notes.table.payroll_title'), text: t('budget_control.notes.table.payroll_text') },
          { tag: 'rule', title: t('budget_control.notes.table.existencial_title'), text: t('budget_control.notes.table.existencial_text') },
          { tag: 'form', title: t('budget_control.notes.table.extra_title'), text: t('budget_control.notes.table.extra_text') },
        ]}
      />
    </section>
  );
}
